import * as React from 'react'
import { useState } from 'react'

import {
  Box,
  Button,
  Card,
  Form,
  Section,
  Text,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { Form as FactorialForm } from 'factorial-form'
import moment from 'moment'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/DayPicker'
import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import LabeledInput from 'modules/core/components/LabeledInput'
import Select from 'modules/core/components/Select'
import TextField from 'modules/core/components/TextField'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useEffect } from 'modules/core/hooks/useEffect'
import { convertDate, dateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { nearestPastHalfHour } from 'modules/reviews/lib/nearestPastHalfHour'
import type Review from 'modules/reviews/store/models/v2/Review'
import type { IReview } from 'modules/reviews/store/models/v2/Review'

import ChangeDurationInDaysReviewModal from './ChangeDurationInDaysReviewModal'
import { DEFAULT_FREQUENCY_OPTION, frequencySelectOptions } from './CycleOptions'
import HourPicker from './HourPicker'

const updateForm = (review: IReview, form: FactorialForm) => {
  const startOnUtcDateTime = moment.utc(review.start_on + ' ' + review.scheduled_time).local()

  const start_on = dateToString(startOnUtcDateTime, 'ui')
  const scheduled_time = startOnUtcDateTime.format('HH:mm')
  const end_on = review.end_on ? dateToString(moment(review.end_on), 'ui') : null
  const duration_in_days = review.duration_in_days
  const frequency = review.frequency

  form.setValues({ start_on: start_on })
  form.setValues({ end_on: end_on })
  form.setValues({ scheduled_time: scheduled_time })
  form.setValues({ duration_in_days: duration_in_days })
  form.setValues({ frequency: frequency })
}

export interface ReviewFactorialFormSchema {
  start_on: string
  scheduled_time: string
  duration_in_days: string
  frequency: string
  end_on: string
}

export const factorialFormFromReview = (review: Review): ReviewFactorialFormSchema => {
  const startOnUtcDateTime = review.startOnUtcDateTime

  const reviewFormValues = {
    start_on: startOnUtcDateTime
      ? dateToString(startOnUtcDateTime.local(), 'ui')
      : dateToString(moment().startOf('day'), 'ui'),
    scheduled_time: startOnUtcDateTime
      ? review.startOnUtcDateTime.local().format('HH:mm')
      : nearestPastHalfHour().format('HH:mm'),
    duration_in_days: review.get('duration_in_days') ? review.get('duration_in_days') : '',
    frequency: review.get('frequency') ? review.get('frequency') : DEFAULT_FREQUENCY_OPTION,
    end_on: review.get('end_on') ? dateToString(moment(review.get('end_on')), 'ui') : '',
  }
  return reviewFormValues
}

const prepareScheduleUTCRequestData = (form: FactorialForm) => {
  const startDayFromUI = convertDate(form.get('start_on').value, 'ui', 'backend')
  const startDateTimeLocal = moment(startDayFromUI + ' ' + form.get('scheduled_time').value).local()

  return {
    start_on: dateToString(moment.utc(startDateTimeLocal), 'backend'),
    scheduled_time: moment.utc(startDateTimeLocal).format('HH:mm'),
    duration_in_days: form.get('duration_in_days').value
      ? form.get('duration_in_days').value
      : null,
    frequency: form.get('frequency').value ? form.get('frequency').value : null,
    end_on: form.get('end_on').value
      ? convertDate(form.get('end_on').value, 'ui', 'backend')
      : null,
  }
}

interface Props {
  review: Review
}

function CycleConfiguration({ review }: Props) {
  const initalFormValues = factorialFormFromReview(review)

  const form = React.useRef(
    new FactorialForm(initalFormValues, {
      start_on: 'string',
      scheduled_time: 'string',
      duration_in_days: 'string',
      frequency: 'string',
      end_on: 'string',
    })
  ).current

  const startDate = form.get('start_on').value
  const durationInDays = form.get('duration_in_days').value

  const [changeDurationInDaysReviewModalOpen, toggleChangeDurationInDaysReviewModal] =
    useToggle(false)

  const { running, performAction: submit } = useAsyncAction(async () => {
    const requestData = prepareScheduleUTCRequestData(form)
    const result = await review.rpc('schedule', requestData)
    updateForm(result, form)
    form.cleanAll()
    review.fetch()
  })

  useEffect(() => {
    const duration_in_days = review.attributes.duration_in_days
    const end_on = review.attributes.end_on
      ? dateToString(moment(review.attributes.end_on), 'ui')
      : null

    form.setValues({ duration_in_days: duration_in_days })
    form.setValues({ end_on: end_on })
    form.cleanAll()
  }, [review.attributes.duration_in_days, review.attributes.end_on])

  const isCycle = review.get('frequency') !== 'single'
  const [open, toggleOpen] = useState(isCycle)
  const toggleRepeatReview = (repeat: boolean) => {
    toggleOpen(repeat)
    form.setValues(
      repeat
        ? { frequency: 'monthly' }
        : {
            frequency: 'single',
            end_on: review.attributes.end_on
              ? dateToString(moment(review.attributes.end_on), 'ui')
              : null,
          }
    )
    form.setValues(
      repeat ? { duration_in_days: '' } : { duration_in_days: review.attributes.duration_in_days }
    )
  }
  const tomorrow = moment().startOf('day').add(1, 'days')
  const cycleOptions = frequencySelectOptions()
  const isSingleReviewSelected = form.get('frequency').value === 'single'
  const isReviewActiveAndCycleHasAnswers = review.isActive && review.cycleHasAnswers
  const isActiveOrFinished = review.isActive || review.isFinished
  const isFinished = review.isFinished

  const userUpdatesDurationInDays = durationInDays != review.attributes.duration_in_days

  const calculateSingleReviewEndOn = () => {
    const formattedDate = stringToDate(startDate, 'ui')
    return moment(formattedDate).add(Number(durationInDays), 'days').format('L')
  }

  const showConfirmationModalOrSubmit = () => {
    if (isReviewActiveAndCycleHasAnswers && review.isDaysToAnswerSet && userUpdatesDurationInDays) {
      toggleChangeDurationInDaysReviewModal(true)()
    } else {
      submit()
    }
  }

  return (
    <Section
      illustration="calendar-clock"
      title={i18n.t('surveys.review.settings.cycle_configuration.title')}
      description={i18n.t('surveys.review.settings.cycle_configuration.description')}
    >
      <Form onSubmit={() => showConfirmationModalOrSubmit()} padding={null}>
        <Box gap="s32">
          <Box
            gap="s16"
            aria-label="Set start date configuration"
            background="grey200"
            borderRadius={{ all: 'abs008' }}
            width="full"
            padding="s32"
          >
            <Box gap="s8">
              <DayPicker
                label={i18n.t('surveys.review.settings.cycle_configuration.frequency.start_day')}
                minDate={moment().startOf('day')}
                value={startDate}
                onChange={(date) => form.setValues({ start_on: date })}
                readonly={isActiveOrFinished}
              />
              <HourPicker
                label={i18n.t('surveys.review.settings.cycle_configuration.frequency.start_time')}
                time={form.get('scheduled_time').value}
                allowCropping={review.isDraft}
                date={startDate}
                readonly={isActiveOrFinished}
                onChange={(time) => form.setValues({ scheduled_time: time })}
              />
            </Box>
            <Box gap="s8">
              <TextField
                placeholder={i18n.t(
                  'surveys.review.settings.cycle_configuration.frequency.days_to_answer.placeholder'
                )}
                type="number"
                min={0}
                readonly={isFinished}
                secondaryLabel={
                  <InformationTooltip
                    information={i18n.t(
                      'surveys.review.settings.cycle_configuration.frequency.days_to_answer.tooltip'
                    )}
                    icon="question"
                  />
                }
                value={durationInDays}
                onChange={(value) =>
                  form.setValues({ duration_in_days: Number(value) ? Number(value) : '' })
                }
                label={i18n.t(
                  'surveys.review.settings.cycle_configuration.frequency.days_to_answer.label'
                )}
              />
              {isSingleReviewSelected && !!durationInDays && (
                <Text.Normal size="200">
                  {i18n.t(
                    'surveys.review.settings.cycle_configuration.frequency.calculated_end_on',
                    {
                      review_end_on: calculateSingleReviewEndOn(),
                    }
                  )}
                </Text.Normal>
              )}
            </Box>
          </Box>
          <Box gap="s16" aria-label="Set frequency configuration" width="full">
            <Card>
              <Card.Title>
                {i18n.t('surveys.review.settings.cycle_configuration.frequency.title')}
              </Card.Title>
              <Card.Description>
                {i18n.t('surveys.review.settings.cycle_configuration.frequency.description')}
              </Card.Description>
              <Card.ExpandableContent
                onExpandChange={toggleRepeatReview}
                expanded={open}
                disabled={isActiveOrFinished}
              >
                <Box gap="s16" paddingX="s16" paddingY="s8">
                  <Select
                    readonly={isActiveOrFinished}
                    label={i18n.t('surveys.form.start.form.labels.schedule')}
                    value={form.get('frequency').value}
                    options={cycleOptions}
                    onChange={(value) => form.setValues({ frequency: value })}
                  />
                  <LabeledInput
                    readonly={isFinished}
                    isLegend
                    label={i18n.t('surveys.review.settings.cycle_configuration.repeat_until.label')}
                    secondaryLabel={
                      <InformationTooltip
                        information={i18n.t('surveys.form.start.form.labels.information')}
                        icon="question"
                      />
                    }
                  >
                    <DayPicker
                      readonly={isFinished}
                      minDate={tomorrow}
                      value={form.get('end_on').value}
                      onChange={(date) => form.setValues({ end_on: date })}
                      placeholder={i18n.t(
                        'surveys.review.settings.cycle_configuration.repeat_until.placeholder'
                      )}
                      withNullLabel={i18n.t('surveys.form.start.form.labels.no_due_date')}
                      withNull
                    />
                  </LabeledInput>
                </Box>
              </Card.ExpandableContent>
            </Card>
          </Box>
          {form.isDirty && (
            <Box alignItems="flexEnd">
              <Button disabled={running} submit>
                {i18n.t('common.save')}
              </Button>
            </Box>
          )}
        </Box>
      </Form>
      {changeDurationInDaysReviewModalOpen && (
        <ChangeDurationInDaysReviewModal
          form={form}
          review={review}
          onClose={toggleChangeDurationInDaysReviewModal(false)}
          onSubmit={submit}
          running={running}
          isSingleReviewSelected={isSingleReviewSelected}
        />
      )}
    </Section>
  )
}

export default observer(CycleConfiguration)
